import clsx from 'clsx'
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from 'framer-motion'
// eslint-disable-next-line no-restricted-imports
import Image from 'next/image'
import { RefObject, useEffect, useRef, useState } from 'react'
import BookingWidget from '../BookingWidget/BookingWidget'
import Button from '../Button/Button'
import Header from '../Header/Header'
import { HomepageHeroProps, Steps } from './HomepageHero'

interface HomepageHeroMobileProps extends HomepageHeroProps {
  clientHeight: number
  siteHeaderHeight: number
  sectionRef: RefObject<HTMLElement | null>
}

export default function HomepageHeroMobile({
  header,
  subheader,
  image,
  imageAlt,
  button,
  stepsHeader,
  steps,
  bookingWidget,
  clientHeight,
  siteHeaderHeight,
  sectionRef,
}: HomepageHeroMobileProps) {
  const contentBRef = useRef<HTMLDivElement>(null)
  const safeSteps = Array.isArray(steps)
    ? steps.filter((step): step is Steps => Boolean(step))
    : []
  const getInitialHeaderPadding = () => {
    if (typeof window === 'undefined') {
      return siteHeaderHeight
    }
    return window.innerWidth < 410 ? siteHeaderHeight / 3 : siteHeaderHeight
  }
  const [headerPadding, setHeaderPadding] = useState(getInitialHeaderPadding)
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false)
  const heroImageAlt = imageAlt ?? 'Hero background image'

  useEffect(() => {
    if (typeof window === 'undefined') return
    const recalc = () => {
      setHeaderPadding(
        window.innerWidth < 410 ? siteHeaderHeight / 3 : siteHeaderHeight,
      )
    }
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [siteHeaderHeight])

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['1 1', '0 0'],
  })
  const bgScale = useTransform(scrollYProgress, [1, 0], [1, 1.1])
  const bgBlur = useTransform(
    scrollYProgress,
    [1, 0.9, 0],
    ['blur(0px)', 'blur(0px)', 'blur(10px)'],
  )
  const contentATopEnd = useTransform(scrollYProgress, [1, 0.7], ['0%', '-20%'])
  const contentBTopEnd = useTransform(
    scrollYProgress,
    [0.9, 0.7],
    ['0%', '-20%'],
  )

  // as you scroll *into* the hero, ramp from light→dark:
  const overlayBlack = useTransform(scrollYProgress, [1, 0], [0, 0.4])
  const overlayColor = useTransform(scrollYProgress, [1, 0], [0.3, 0.5])
  const overlayGradient = useMotionTemplate`
    linear-gradient(0deg, rgba(0,0,0,${overlayBlack}) 0%, rgba(0,0,0,${overlayBlack}) 100%),
    linear-gradient(0deg, rgba(28,41,56,${overlayColor}) 0%, rgba(28,41,56,${overlayColor}) 100%)
  `

  useEffect(() => {
    setIsBackgroundLoaded(false)
  }, [image])

  const markImageReady = (): void => {
    setIsBackgroundLoaded(true)
  }

  return (
    <>
      <div className="absolute left-0 top-0 w-full h-full">
        <div
          className="sticky top-0 w-screen h-screen"
          style={{
            height: clientHeight,
            paddingTop: siteHeaderHeight,
            marginTop: siteHeaderHeight * -1,
          }}
        >
          <div className="relative w-full h-full">
            <motion.div
              className={clsx(
                'absolute left-1/2 top-1/2 w-screen h-screen',
                'relative',
              )}
              style={{
                height: clientHeight - siteHeaderHeight,
                scale: bgScale,
                filter: bgBlur,
                translateX: '-50%',
                translateY: '-50%',
                willChange: 'transform, filter',
                backfaceVisibility: 'hidden',
                backgroundImage: `url(${image})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                // opacity: isBackgroundLoaded ? 1 : 0,
              }}
            >
              <Image
                src={image}
                alt={heroImageAlt}
                fill
                priority
                fetchPriority="high"
                loading="eager"
                decoding="async"
                sizes="100vw"
                style={{
                  opacity: isBackgroundLoaded ? 1 : 0,
                  transition: 'opacity 500ms ease',
                }}
                className="absolute inset-0 h-full w-full object-cover"
                onLoadingComplete={markImageReady}
                onError={markImageReady}
              />
              <motion.div
                aria-hidden
                className="absolute inset-0"
                style={{ backgroundImage: overlayGradient }}
              />
            </motion.div>
            <div
              className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(26,39,58,0.8),_rgba(10,16,28,1))] transition-opacity duration-500"
              style={{ opacity: isBackgroundLoaded ? 0 : 1 }}
              aria-hidden
            />
          </div>
        </div>
      </div>
      <div
        className="relative"
        style={{
          marginTop: headerPadding,
        }}
      >
        <div className="px-6 md:pt-20 mx-auto text-white">
          <motion.div style={{ translateY: contentATopEnd }}>
            <Header
              as="h1"
              overrideClassName
              className="type-display-1 !font-normal [&_span]:!font-semibold mb-5 md:mb-[2.375rem]"
            >
              {header}
            </Header>
            <p className="type-body-2">{subheader}</p>
          </motion.div>
          {(bookingWidget || button) && (
            <motion.div
              className="pt-8 md:pt-20"
              style={{ translateY: contentBTopEnd }}
            >
              {bookingWidget && <BookingWidget {...bookingWidget} />}
              {button && (
                <Button
                  onClick={button.onClick}
                  variant="secondary"
                  className="mt-10"
                >
                  {button.text}
                </Button>
              )}
            </motion.div>
          )}
          {safeSteps.length > 0 && (
            <motion.div
              className="py-10 w-full"
              // style={{ opacity: contentCOpacity }}
              ref={contentBRef}
            >
              <motion.h2 className="text-[32px] !font-normal mb-10 text-center">
                {stepsHeader}
              </motion.h2>
              <div className="flex flex-col gap-y-16 items-start">
                {safeSteps.map(({ header, subheader }, key) => (
                  <motion.div
                    className="flex-grow basis-0"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      transition: { delay: 2 + key / 3, duration: 1 },
                    }}
                    viewport={{ once: true, amount: 0.4 }}
                    key={key}
                  >
                    <div className="inline-block type-body-7 px-[1.40625rem] py-[0.1875rem] bg-theme-accessibility rounded-[2.5rem] mb-8">
                      STEP {key + 1}
                    </div>
                    <Header
                      as="h3"
                      overrideClassName
                      className="type-headline-2 mb-2"
                    >
                      {header}
                    </Header>
                    <p className="type-body-4">{subheader}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  )
}
